/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs error details, and displays a fallback UI.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary level="feature" featureName="Scanner">
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */

import { Component, ReactNode } from "react";
import ErrorFallback from "./ErrorFallback";
import { ErrorBoundaryProps, ErrorBoundaryState, ErrorLogEntry } from "./types";
import { Sentry, captureException } from "../../config/sentry";

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    };
  }

  static defaultProps = {
    showDetails: process.env.NODE_ENV === 'development',
    level: 'feature' as const,
  };

  /**
   * Update state when an error is caught
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error details when an error is caught
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Generate unique event ID for tracking
    const eventId = this.generateEventId();

    // Update state with error details
    this.setState({
      errorInfo,
      eventId,
    });

    // Create error log entry
    const logEntry: ErrorLogEntry = {
      timestamp: new Date().toISOString(),
      level: this.props.level || 'feature',
      featureName: this.props.featureName,
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      componentStack: errorInfo.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // Log to console
    this.logErrorToConsole(logEntry, error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you would send this to an error monitoring service
    // Example: Sentry, LogRocket, etc.
    this.logErrorToMonitoring(logEntry, eventId);
  }

  /**
   * Generate unique event ID for error tracking
   */
  private generateEventId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Log error to console with formatted output
   */
  private logErrorToConsole(
    logEntry: ErrorLogEntry,
    error: Error,
    errorInfo: React.ErrorInfo
  ): void {
    const { level, featureName } = this.props;

    console.group(
      `%c🚨 Error Boundary Caught [${level}${featureName ? `: ${featureName}` : ''}]`,
      'color: #f44336; font-weight: bold; font-size: 14px;'
    );

    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.log('Log Entry:', logEntry);
    console.log('Event ID:', this.state.eventId);

    console.groupEnd();
  }

  /**
   * Send error to monitoring service
   */
  private logErrorToMonitoring(logEntry: ErrorLogEntry, eventId: string): void {
    // Send to Sentry with additional context
    try {
      // Create an Error object from the logged error info
      const error = new Error(logEntry.error.message);
      error.name = logEntry.error.name || 'Error';
      error.stack = logEntry.error.stack;

      // Capture with Sentry, including all context
      Sentry.withScope((scope) => {
        // Set error boundary context
        scope.setTag('errorBoundary', true);
        scope.setTag('level', logEntry.level);
        scope.setTag('eventId', eventId);

        if (logEntry.featureName) {
          scope.setTag('feature', logEntry.featureName);
        }

        // Set additional context
        scope.setContext('errorBoundary', {
          level: logEntry.level,
          featureName: logEntry.featureName,
          eventId,
          timestamp: logEntry.timestamp,
        });

        scope.setContext('browser', {
          userAgent: logEntry.userAgent,
          url: logEntry.url,
        });

        // Add component stack as extra data
        scope.setExtra('componentStack', logEntry.componentStack);

        // Capture the exception
        Sentry.captureException(error);
      });

      if (process.env.NODE_ENV === 'development') {
        console.log(
          '%c[Dev Mode] Error sent to Sentry monitoring',
          'color: #4caf50; font-style: italic;',
          { eventId }
        );
      }
    } catch (sentryError) {
      // If Sentry fails, don't break the app
      console.error('Failed to send error to Sentry:', sentryError);
    }
  }

  /**
   * Reset error state to allow retry
   */
  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    });
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, showDetails, level, featureName } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Use default ErrorFallback component
      return (
        <ErrorFallback
          error={error}
          errorInfo={errorInfo}
          resetError={this.resetError}
          showDetails={showDetails}
          level={level}
          featureName={featureName}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary;
